import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Scale, Users, ThumbsUp, ThumbsDown, MinusCircle, Lightbulb, ChevronDown, ChevronUp, Download, Star, Mail } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function AdminCommittee() {
  const [members, setMembers] = useState([]);
  const [votes, setVotes] = useState([]);
  const [open, setOpen] = useState({});

  const load = useCallback(() => {
    api.get("/admin/committee/members").then((r) => setMembers(r.data)).catch(() => {});
    api.get("/admin/committee/votes").then((r) => setVotes(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const totalVotes = votes.reduce((s, v) => s + v.total, 0);

  const remindNonVoters = async (v) => {
    if (!window.confirm(`Relancer les membres du comité qui n'ont pas encore voté sur « ${v.title} » ?`)) return;
    try {
      const { data } = await api.post("/admin/committee/remind", { project_id: v.project_id });
      toast.success(data.reminded > 0 ? `${data.reminded} membre(s) relancé(s).` : "Tous les membres ont déjà voté.");
    } catch { toast.error("Impossible d'envoyer la relance."); }
  };
  const sendDigest = async () => {
    if (!window.confirm("Envoyer la synthèse hebdomadaire à tous les membres du comité ?")) return;
    try {
      const { data } = await api.post("/admin/committee/weekly-digest", {});
      toast.success(`Synthèse envoyée à ${data.sent}/${data.members} membre(s).`);
    } catch { toast.error("Impossible d'envoyer la synthèse."); }
  };

  const exportCsv = () => {
    const rows = [["Idée", "Référence", "Statut", "Ambassadeur", "Vote", "Commentaire", "Date"]];
    votes.forEach((v) => {
      if (v.votes.length === 0) rows.push([v.title, v.ref, v.status, "—", "—", "—", "—"]);
      v.votes.forEach((vv) => rows.push([v.title, v.ref, v.status, vv.name, vv.vote, vv.comment, vv.at ? new Date(vv.at).toLocaleString("fr-FR") : ""]));
    });
    const csv = rows.map((r) => r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a"); a.href = url; a.download = "avis-comite-aca.csv"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Export CSV téléchargé.");
  };

  return (
    <div className="mx-auto max-w-6xl">
      {/* En-tête */}
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-surface p-8">
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-amber-400/15 blur-[100px]" />
        <div className="pointer-events-none absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan/15 blur-[110px]" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-amber-400"><Scale size={14} /> Comité de décision</div>
          <h1 className="mt-3 font-display text-3xl font-900 text-white sm:text-4xl">Comité des décisions</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink-secondary">
            Les ambassadeurs membres du comité participent à la sélection des idées : ils donnent leur avis
            (pour, contre, abstention) sur les idées en étude, présélectionnées et finalistes. Intégrez ou
            retirez un ambassadeur du comité depuis l'onglet « Ambassadeurs ».
          </p>
          <button onClick={sendDigest} data-testid="committee-digest" className="aca-btn-secondary mt-5 inline-flex items-center gap-2 text-white">
            <Mail size={15} /> Envoyer la synthèse hebdo
          </button>
        </div>
      </div>

      {/* Cartes récap */}
      {/* Section « ensemble » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-ensemble">Vue d'ensemble</div>
      <div className="mt-6 grid gap-4 sm:grid-cols-3" data-testid="committee-panel-ensemble">
        <Card icon={Users} c="#00F0FF" l="Membres du comité" v={members.length} testid="committee-stat-members" />
        <Card icon={Lightbulb} c="#FFD60A" l="Idées soumises au vote" v={votes.length} testid="committee-stat-ideas" />
        <Card icon={Scale} c="#BF5AF2" l="Avis exprimés" v={totalVotes} testid="committee-stat-votes" />
      </div>
      </>

      {/* Membres */}
      {/* Section « membres » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-membres">Membres</div>
      <div className="mt-6 rounded-xl border border-white/10" data-testid="committee-panel-membres">
        {members.length === 0 ? (
          <p className="p-8 text-center text-sm text-ink-tertiary" data-testid="committee-members-empty">
            Aucun membre pour l'instant. Depuis l'onglet « Ambassadeurs », cliquez sur l'icône balance pour intégrer un ambassadeur au comité.
          </p>
        ) : (
          <div className="divide-y divide-white/5">
            {members.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 p-4" data-testid={`committee-member-${m.id}`}>
                <div className="min-w-0">
                  <div className="truncate font-600 text-white">
                    {m.permanent ? (m.invite_name || "Code de groupe") : ((m.first_name || m.last_name) ? `${m.first_name || ""} ${m.last_name || ""}`.trim() : (m.invite_name || m.code))}
                    {m.permanent && <span className="ml-2 rounded-full bg-electric/20 px-2 py-0.5 text-[10px] uppercase text-electric">{m.member_count} membre(s)</span>}
                  </div>
                  <div className="truncate text-xs text-ink-secondary">{m.email || m.invite_email || "—"} · <span className="font-mono text-cyan">{m.code}</span></div>
                </div>
                <span className="shrink-0 text-xs text-ink-secondary">{m.votes_count || 0} avis</span>
              </div>
            ))}
          </div>
        )}
      </div>
      </>

      {/* Idées au vote */}
      {/* Section « avis » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-avis">Avis par idée</div>
      <div data-testid="committee-panel-avis">
      <div className="mt-6 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">Avis du comité par idée</div>
        {votes.length > 0 && (
          <button onClick={exportCsv} data-testid="committee-export-csv" className="aca-btn-secondary inline-flex items-center gap-1.5 text-white">
            <Download size={14} /> Exporter (CSV)
          </button>
        )}
      </div>
      <div className="mt-4 space-y-3">
        {votes.length === 0 && (
          <p className="rounded-xl border border-white/10 bg-surface p-8 text-center text-sm text-ink-tertiary" data-testid="committee-votes-empty">
            Aucune idée en étude, présélectionnée ou finaliste pour l'instant.
          </p>
        )}
        {votes.map((v) => (
          <motion.div key={v.project_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="overflow-hidden rounded-xl border border-white/10 bg-surface" data-testid={`committee-idea-${v.project_id}`}>
            <button onClick={() => setOpen({ ...open, [v.project_id]: !open[v.project_id] })} className="flex w-full items-center justify-between gap-4 p-5 text-left">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate font-display text-base font-700 text-white">{v.title || "Idée sans titre"}</span>
                  {v.recommended && <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-green-500/15 px-2 py-0.5 text-[10px] font-600 uppercase text-green-400" data-testid={`committee-recommended-${v.project_id}`}><Star size={10} /> Recommandée</span>}
                </div>
                <div className="text-xs text-ink-secondary">{v.ref} · {v.status} · {v.category}</div>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <Tally icon={ThumbsUp} c="#30D158" n={v.tally.pour} />
                <Tally icon={ThumbsDown} c="#FF453A" n={v.tally.contre} />
                <Tally icon={MinusCircle} c="#8A94A8" n={v.tally.abstention} />
                {open[v.project_id] ? <ChevronUp size={18} className="text-ink-tertiary" /> : <ChevronDown size={18} className="text-ink-tertiary" />}
              </div>
            </button>
            {open[v.project_id] && (
              <div className="border-t border-white/10 p-5">
                <div className="mb-4 flex justify-end">
                  <button onClick={() => remindNonVoters(v)} data-testid={`committee-remind-${v.project_id}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-cyan/40 px-4 py-2 text-[11px] font-600 uppercase tracking-wide text-cyan transition-colors hover:bg-cyan/10">
                    <MinusCircle size={13} /> Relancer les non-votants
                  </button>
                </div>
                {v.votes.length === 0 ? (
                  <p className="text-sm text-ink-tertiary">Aucun avis exprimé pour cette idée.</p>
                ) : (
                  <div className="space-y-3">
                    {v.votes.map((vv, i) => (
                      <div key={i} className="rounded-lg border border-white/10 bg-void p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-white">{vv.name || "Ambassadeur"}</span>
                          <VoteBadge vote={vv.vote} />
                        </div>
                        {vv.comment && <p className="mt-1 text-sm text-ink-secondary">{vv.comment}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        ))}
      </div>
      </div>
      </>
    </div>
  );
}

function Card({ icon: Icon, c, l, v, testid }) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-white/10 bg-void p-5" data-testid={testid}>
      <span className="absolute inset-x-0 top-0 h-0.5" style={{ background: c }} />
      <span className="flex h-11 w-11 items-center justify-center rounded-lg" style={{ background: `${c}1f`, color: c }}><Icon size={20} /></span>
      <div className="mt-4 font-display text-3xl font-900 tabular-nums text-white">{v}</div>
      <div className="mt-1 text-sm text-ink-secondary">{l}</div>
    </div>
  );
}

function Tally({ icon: Icon, c, n }) {
  return <span className="inline-flex items-center gap-1 text-sm tabular-nums" style={{ color: c }}><Icon size={15} /> {n}</span>;
}

function VoteBadge({ vote }) {
  const map = {
    pour: { l: "Pour", c: "bg-green-500/15 text-green-400" },
    contre: { l: "Contre", c: "bg-red-500/15 text-red-400" },
    abstention: { l: "Abstention", c: "bg-white/10 text-ink-secondary" },
  };
  const m = map[vote] || map.abstention;
  return <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-600 uppercase ${m.c}`}>{m.l}</span>;
}
